import { Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { Button, QuestionnaireFormData, questionnaireHasFilledAnswers } from 'common';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { PostSignupFormData } from '../schemas';
import { SubmissionDocKey } from './buildDocSubmissionPlan';

export type SubmissionStatus = 'idle' | 'submitting' | 'success' | 'draft' | 'error' | 'skipped';

export interface SubmissionState {
    status: SubmissionStatus;
    errorMessage?: string;
}

export interface ReviewRowLabel {
    key: SubmissionDocKey;
    label: string;
    attachmentNames?: string[];
}

/** e.g. "2 attachments · coi.pdf, endorsement.pdf" */
function attachmentSummary(names: string[]): string {
    const count = names.length === 1 ? '1 attachment' : `${names.length} attachments`;
    return `${count} · ${names.join(', ')}`;
}

export interface ReviewAndSubmitStepProps {
    rows: ReviewRowLabel[];
    states: Record<SubmissionDocKey, SubmissionState>;
    onRetry: (key: SubmissionDocKey, formData: PostSignupFormData) => void;
    onSkip: (key: SubmissionDocKey) => void;
    questionnaires: QuestionnaireFormData[];
    actionsDisabled?: boolean;
}

function statusLabel(status: SubmissionStatus): string {
    switch (status) {
        case 'submitting':
            return 'Sending...';
        case 'success':
            return 'Sent';
        case 'draft':
            return 'Saved as draft';
        case 'error':
            return 'Failed';
        case 'skipped':
            return 'Skipped';
        default:
            return 'Pending';
    }
}

function statusChipColor(status: SubmissionStatus): 'success' | 'error' | 'warning' | 'default' {
    switch (status) {
        case 'success':
            return 'success';
        case 'draft':
            return 'warning';
        case 'error':
            return 'error';
        default:
            return 'default';
    }
}

export function ReviewAndSubmitStep({
    rows,
    states,
    onRetry,
    onSkip,
    questionnaires,
    actionsDisabled = false,
}: ReviewAndSubmitStepProps) {
    const { getValues } = useFormContext<PostSignupFormData>();
    const formQuestionnaires = useWatch<PostSignupFormData, 'questionnaires'>({ name: 'questionnaires' });

    const visibleRows = useMemo(() => {
        const answers = formQuestionnaires ?? [];
        const questionnairesBySubTypeId = new Map(
            questionnaires.map((questionnaire) => [
                questionnaire.businessDocTypeViewModel.BusinessDocSubTypeId,
                questionnaire,
            ])
        );

        return rows.filter((row) => {
            if (!row.key.startsWith('questionnaire:')) {
                return true;
            }
            const subTypeId = Number(row.key.slice('questionnaire:'.length));
            const questionnaire = questionnairesBySubTypeId.get(subTypeId);
            const entry = answers.find((item) => item.businessDocSubTypeId === subTypeId);
            if (!questionnaire || !entry) {
                return false;
            }
            const hasAnswers = questionnaireHasFilledAnswers(questionnaire.items, entry.answers);
            const hasAttachments = (row.attachmentNames?.length ?? 0) > 0;
            return hasAnswers || hasAttachments;
        });
    }, [formQuestionnaires, questionnaires, rows]);

    if (visibleRows.length === 0) {
        return null;
    }

    const handleRetry = (key: SubmissionDocKey) => () => onRetry(key, getValues());
    const handleSkip = (key: SubmissionDocKey) => () => onSkip(key);

    return (
        <Stack spacing={3} p={{ xs: 2, sm: 0 }}>
            {/* TODO(product): review copy for the post-signup Submit step */}
            <Typography>Check the list below, then click Submit to send these documents to your customer.</Typography>
            <Stack spacing={2}>
                {visibleRows.map(({ key, label, attachmentNames }) => {
                    const state = states[key] ?? { status: 'idle' };
                    const isError = state.status === 'error';
                    const isDraft = state.status === 'draft';
                    const isSubmitting = state.status === 'submitting';
                    const canRetry = !actionsDisabled && (isError || isDraft);
                    const showMessage = (isError || isDraft) && !!state.errorMessage;
                    return (
                        <Stack key={key} spacing={0.5} py={1.5}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Stack sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography noWrap>{label}</Typography>
                                    {attachmentNames != null && attachmentNames.length > 0 && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            noWrap
                                            title={attachmentNames.join(', ')}
                                        >
                                            {attachmentSummary(attachmentNames)}
                                        </Typography>
                                    )}
                                </Stack>
                                {isSubmitting && <CircularProgress size={16} />}
                                {canRetry && (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Button variant="secondary" onClick={handleRetry(key)}>
                                            Retry
                                        </Button>
                                        {isError && (
                                            <Button variant="text" onClick={handleSkip(key)}>
                                                Skip
                                            </Button>
                                        )}
                                    </Stack>
                                )}
                                <Chip
                                    label={statusLabel(state.status)}
                                    color={statusChipColor(state.status)}
                                    size="small"
                                />
                            </Stack>
                            {showMessage && (
                                <Typography variant="body2" color={isDraft ? 'warning.main' : 'error.main'}>
                                    {state.errorMessage}
                                </Typography>
                            )}
                        </Stack>
                    );
                })}
            </Stack>
        </Stack>
    );
}
